import { XY } from './V2.js';
import { Numr } from './Numr.js';

export const Rr = { //radian + winkel stuff
    pp: Math.PI * 2,
    normalize: function (angle: number) {
        return Rr.get.roll(angle);
    },
    vectorize: function (angle: number) {
        return Math.atan2(Math.sin(angle), Math.cos(angle));
    },
    from: {
        points: function (a: XY, b: XY): number { //radian angle between 2 points
            return Math.atan2(b.y - a.y, b.x - a.x);
        },
        points_n: function (a: XY, b: XY): number { //radian angle between 2 points, returns normalised 0-1
            return Rr.get.n(Math.atan2(b.y - a.y, b.x - a.x));
        },
        v: function (a: XY, b: XY, c: XY): number { // returns clockwise angle from a to c, b is in the middle (V shape)
            const a1 = Math.atan2((b.y - a.y), (b.x - a.x));
            const a2 = Math.atan2((c.y - b.y), (c.x - b.x));
            const angle = a2 - a1 + Math.PI;
            return Rr.get.roll(angle);
        },
        degree: function (degrees: number) { //converts degrees into radian
            return degrees * (Math.PI / 180);
        },
        float: function (f: number) { //converts radian into 0-1
            return f * Rr.pp;
        }
    },
    get: {
        r: function (n: number): number { //converts float 0-1 into radian
            return n * Rr.pp;
        },
        n: function (radian: number): number { //converts float 0-1 into radian
            return radian / Rr.pp;
        },
        degree: function (radian: number): number { //converts radian into degree
            return radian * 180 / Math.PI;
        },
        position: function (point: XY, radian: number, distance = 0.5): XY {
            return { x: point.x + Math.cos(radian) * distance, y: point.y + Math.sin(radian) * distance };
        },
        point: function (point: XY, angle: number, distance = 0.5): XY { //angle = 0-1
            const r = angle * Rr.pp;
            return { x: point.x + Math.cos(r) * distance, y: point.y + Math.sin(r) * distance };
        },
        orbit: function (angle: number, distance = 0.5): XY { //same as get.point but without xy offset
            const r = (angle - 0.25) * Rr.pp;
            return { x: Math.cos(r) * distance, y: Math.sin(r) * distance };
        },
        roll: function (rad: number): number {
            return rad - Math.floor(rad / Rr.pp) * Rr.pp;
        },
        total: function (turns: number, now: number): number {
            return turns * Rr.pp + Rr.normalize(now);
        },
        turns: function (before: number, now: number, turn: number, tolerance = Math.PI): number { //checks how many times a r goes in circle
            const nowN = Rr.normalize(now);
            const beforeN = Rr.normalize(before);
            if (Math.abs(nowN - beforeN) > tolerance) (nowN < Math.PI) ? turn++ : turn--;
            return turn;
        },
        turns_float: function (before: number, now: number, turn: number, tolerance = 0.5): number { //checks how many times an angle [0,1] goes in circle
            const nowN = Math.abs(now % 1);
            const beforeN = Math.abs(before % 1);
            if (Math.abs(nowN - beforeN) > tolerance) (nowN < 0.5) ? turn++ : turn--;
            return turn;
        },
        passing: function (aOld: number, aNow: number, bOld: number, bNow: number, turns: number, tolerance = Math.PI): number { //takes two (clock) radians (a and b) and checks how many times now passes limit onto which side. "before" is previous r or now and "turn" how many passes 

            //normalize
            let aNowN = Rr.normalize(aNow);
            let aOldN = Rr.normalize(aOld);
            let bNowN = Rr.normalize(bNow);
            let bOldN = Rr.normalize(bOld);

            //account that R from now could have jumped over 0 mark in this frame
            if (Math.abs(aNowN - aOldN) > tolerance) (aNowN < Math.PI) ? aOldN -= Rr.pp : aOldN += Rr.pp;
            if (Math.abs(bNowN - bOldN) > tolerance) (bNowN < Math.PI) ? bOldN -= Rr.pp : bOldN += Rr.pp;

            //get difference
            const dNow = aNowN - bNowN;
            const dOld = aOldN - bOldN;

            if (dOld > 0 && dNow <= 0) turns--;
            if (dOld < 0 && dNow >= 0) turns++;

            return turns;
        },
        passing_float: function (aOld: number, aNow: number, bOld: number, bNow: number, turns: number, tolerance = 0.5): number { // angles are normalized [0, 1], tolerance is now 0.5 (half a circle)

            // Normalize angles to [0, 1]
            const aNowN = Math.abs(aNow % 1);
            let aOldN = Math.abs(aOld % 1);
            let bNowN = Math.abs(bNow % 1);
            let bOldN = Math.abs(bOld % 1);

            // Account for jumps over the 0-1 boundary
            if (Math.abs(aNowN - aOldN) > tolerance) {
                if (aNowN < 0.5) aOldN -= 1;
                else aOldN += 1;
            }
            if (Math.abs(bNowN - bOldN) > tolerance) {
                if (bNowN < 0.5) bOldN -= 1;
                else bOldN += 1;
            }

            // Get differences
            const dNow = aNowN - bNowN;
            const dOld = aOldN - bOldN;

            // Update pass count based on crossing
            if (dOld > 0 && dNow <= 0) turns--;
            if (dOld < 0 && dNow >= 0) turns++;

            return turns;
        },
        passes: function (aOld: number, aNow: number, bOld: number, bNow: number, tolerance = Math.PI): boolean { //if two radians are passing each other

            //normalize
            let aNowN = Rr.normalize(aNow);
            let aOldN = Rr.normalize(aOld);
            let bNowN = Rr.normalize(bNow);
            let bOldN = Rr.normalize(bOld);

            //account that R from now could have jumped over 0 mark in this frame
            if (Math.abs(aNowN - aOldN) > tolerance) (aNowN < Math.PI) ? aOldN -= Rr.pp : aOldN += Rr.pp;
            if (Math.abs(bNowN - bOldN) > tolerance) (bNowN < Math.PI) ? bOldN -= Rr.pp : bOldN += Rr.pp;

            //get difference
            const dNow = aNowN - bNowN;
            const dOld = aOldN - bOldN;

            if (dOld > 0 && dNow <= 0) return true;
            if (dOld < 0 && dNow >= 0) return true;
            return false;
        },
        difference: function (a: number, b: number): number {
            const diff = Math.abs(a - b);
            const wrappedDiff = Math.abs(Rr.pp - diff);
            return Math.min(diff % Rr.pp, wrappedDiff);
        },
        shortest_difference: function (target: number, current: number): number {
            const diff = target - current;
            return (diff + Math.PI) % (2 * Math.PI) - Math.PI;
        },
        shortest_difference_float: function (current: number, target: number): number { // 0-1
            const diff = (target - current) % 1;
            if (diff > 0.5) return diff - 1;
            if (diff < -0.5) return diff + 1;
            return diff;
        },
        clockwise_difference(angle1: number, angle2: number): number {
            let difference = (angle2 - angle1) % Rr.pp;
            if (difference < 0) difference += 2 * Math.PI;
            return difference;
        },
        clockwise_difference_float(angle1: number, angle2: number): number { //0-1
            let difference = (angle2 - angle1) % 1;
            if (difference < 0) difference += 1;
            return difference;
        },

        clockwise_middle(rad1: number, rad2: number): number { //untested
            const difference = Rr.get.clockwise_difference(rad1, rad2);
            return Rr.normalize(rad1 + difference * 0.5);
        },
        weighted_average_clockwise(rad1: number, weight1: number, rad2: number, weight2: number) { //untested
            const difference = Rr.get.clockwise_difference(rad1, rad2);
            return Rr.normalize(rad1 + difference / (weight1 + weight2) * weight1);
        },
        clamped_range(angle: number, turns: number, range: number, range_offset: number = -0.25, orbit = 1): number { //360 degrees = 1.0, returns angle that is within that range. use turns (from turns_float) to unwrap angle so it clamps without jumping at wrap point
            const unwrapped_angle = angle + turns * orbit;
            const range_low = range_offset - range * 0.5;
            const range_high = range_offset + range * 0.5;
            return Numr.clamp(unwrapped_angle, range_low, range_high);
        },
    },
    check: {
        inRange: function (center: XY, a: XY, b: XY, p: XY): boolean { //if r is between a and b (watching from center), b needs to be clockwise from a
            const r1 = Rr.from.v(a, center, b);
            const r2 = Rr.from.v(a, center, p);
            if (r2 <= r1) return true;
            return false;
        },
    }
};